"use client";

import { useEffect, useState } from "react";
import { CalendarDaysIcon, ContactRoundIcon, FlameIcon } from "lucide-react";
import { clientGet } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { SuperLink } from "@/components/common/super-link";
import { cn } from "@/lib/utils";
import {
  ACTIVITY_CELL_ATTR,
  ActivityTooltip,
  useActivityTooltip,
} from "@/components/exams/exam-learning-activity/activity-tooltip";

// Kafelki tygodnia = te same co w heatmapie aktywności (exam-learning-activity):
// zielone dla nauki wykonanej, niebieskie dla zaplanowanej w przyszłości (0–4).
const GREEN_LEVEL_CLASS: Record<number, string> = {
  0: "bg-zinc-100",
  1: "bg-emerald-200",
  2: "bg-emerald-400",
  3: "bg-emerald-600",
  4: "bg-emerald-800",
};

const BLUE_LEVEL_CLASS: Record<number, string> = {
  0: "bg-zinc-100",
  1: "bg-sky-200",
  2: "bg-sky-400",
  3: "bg-sky-600",
  4: "bg-sky-800",
};

export const ExamStatsBanner = ({
  streak,
  daysToExam,
  today,
  weekDays,
}: {
  streak: number;
  daysToExam: number | null;
  today: string;
  weekDays: {
    label: string;
    level: number;
    isFuture: boolean;
    day: ExamLearningActivityDayVM;
  }[];
}) => {
  // Pozycja w rankingu jest liczona tylko po stronie klienta (GET /exam/ranking),
  // więc dociągamy ją po zamontowaniu — analogicznie do <ExamRanking />.
  const [rankPosition, setRankPosition] = useState<number | null>(null);
  // Ten sam tooltip co w heatmapie aktywności.
  const { hover, onCellEnter, onCellLeave, onCellClick } = useActivityTooltip();

  useEffect(() => {
    let active = true;
    clientGet("exam.ranking", {
      metric: "attempt_answers,sr_answers",
      period: "month",
      categoryId: 0,
    })
      .then((res: ExamRankingVM) => {
        if (active) {
          setRankPosition(res.me?.inRanking ? res.me.position : null);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="-mx-2 mb-4 flex items-center justify-between gap-3 rounded-full border border-transparent bg-white px-2 py-2.5 shadow-sm sm:mx-0 sm:mb-8 sm:gap-6 sm:gap-8 sm:border-zinc-200 sm:px-5 sm:px-8 sm:shadow-none">
      {/* Statystyki — na mobile widoczna tylko passa nauki. */}
      <div className="flex items-center gap-6 sm:gap-8">
        <Stat
          icon={
            <FlameIcon
              size={16}
              className="text-red-600"
              style={{
                filter: "drop-shadow(2px 2px 2px rgba(220,38,38,0.35))",
              }}
            />
          }
          value={streak}
          label="Dni nauki"
          href={`${createPath("exam.stats")}#aktywnosc`}
        />
        {daysToExam !== null && (
          <Stat
            icon={
              <CalendarDaysIcon
                size={16}
                className="text-remedium-blue"
                style={{
                  filter: "drop-shadow(2px 2px 2px rgba(46,53,255,0.35))",
                }}
              />
            }
            value={daysToExam}
            label="Dni do egzaminu"
            className="hidden sm:flex"
            href={createPath("exam.schedule", { examType: null })}
          />
        )}
        {rankPosition !== null && (
          <Stat
            icon={
              <ContactRoundIcon
                size={16}
                className="text-remedium-blue"
                style={{
                  filter: "drop-shadow(2px 2px 2px rgba(46,53,255,0.35))",
                }}
              />
            }
            value={`#${rankPosition}`}
            label="W rankingu"
            className="hidden sm:flex"
            href={`${createPath("exam.stats")}#ranking`}
          />
        )}
      </div>

      {/* Tydzień nauki — kafelek na każdy dzień, dziś/egzamin obwiedziony. */}
      <div className="flex items-center">
        {weekDays.map((weekDay, index) => {
          const { label, level, isFuture, day } = weekDay;
          const palette = isFuture ? BLUE_LEVEL_CLASS : GREEN_LEVEL_CLASS;
          const todayRing = isFuture ? "ring-sky-700" : "ring-emerald-700";
          return (
            <div
              key={label}
              className="flex w-6 flex-col items-center gap-0.5 sm:w-8"
            >
              <span className="text-[10px] text-zinc-500 sm:text-xs">
                {label}
              </span>
              <span
                {...{ [ACTIVITY_CELL_ATTR]: "" }}
                onMouseEnter={onCellEnter(index)}
                onMouseLeave={onCellLeave}
                onClick={onCellClick(index)}
                className={cn(
                  "size-5 rounded-[4px] sm:size-6",
                  palette[level] ?? palette[0],
                  day.isExam &&
                    "ring-2 ring-pink-400 ring-offset-1 ring-offset-white",
                  day.isToday &&
                    cn("ring-2 ring-offset-1 ring-offset-white", todayRing),
                )}
              />
            </div>
          );
        })}
      </div>

      {hover && weekDays[hover.index] && (
        <ActivityTooltip
          day={weekDays[hover.index].day}
          today={today}
          left={hover.left}
          top={hover.top}
          bottom={hover.bottom}
        />
      )}
    </div>
  );
};

const Stat = ({
  icon,
  value,
  label,
  className,
  href,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  className?: string;
  href?: string;
}) => {
  const content = (
    <>
      <div className="flex items-center gap-1">
        {icon}
        <span className="text-sm font-semibold text-zinc-900">{value}</span>
      </div>
      <span className="text-xs font-medium whitespace-nowrap text-zinc-500">
        {label}
      </span>
    </>
  );

  const baseClass = "flex flex-col items-center gap-px rounded-xl px-3 py-1.5";

  if (!href) {
    return <div className={cn(baseClass, className)}>{content}</div>;
  }

  return (
    <SuperLink
      href={href}
      className={cn(
        baseClass,
        "-my-1.5 transition-colors hover:bg-zinc-100 active:bg-zinc-200",
        className,
      )}
    >
      {content}
    </SuperLink>
  );
};
